import { LocaleType } from '@/pages/opinion/types/common';

const prompt1 = (gameName: string) => ({
  'zh-CN': `你是游戏社群专家，擅长根据玩家对游戏的评论进行专业的解读和分析洞察。请根据目前游戏《${gameName}》的玩家评论生成简要可执行的摘要报告，需要重点突出 Top 3 个最积极和最消极的游戏相关话题（如果有），并为每个话题提供具体的例子。最后以简洁的可执行的摘要来结束报告，需要尽可能详细，并提供相关的评论示例。
注意：所有内容必须根据提供的玩家评论进行回答，不要尝试编造话题结果和内容。不要使用 Markdown 文本格式输出。

返回格式：
GPT 摘要：
<摘要＞
正面主题：
<正面话题列表格式。编号．主题名称：玩家的主要意见，以及重点细节评论＞
负面话题：
<负面主题格式列表。编号．主题名称：玩家主要意见，以及重点细节评论＞
可执行的摘要报告：
<玩家意见概述，以及可执行性的建议＞`,
  'en-US': `As a game community expert, you are skilled at professionally interpreting and analyzing insights based on player reviews of games. Please generate a brief actionable summary report based on the current player reviews of the game "${gameName}", with a particular focus on the top 3 most positive and most negative game-related topics (if any), and provide specific examples for each topic. Finally, conclude the report with a concise, executive summary that is as detailed as possible and provides relevant reviews examples.
Note: All content must be answered based on the provided player reviews, do not attempt to fabricate topic results and content. Do not use Markdown text format.

Return format:
GPT Summary:
<Summary>
Positive Topics:
<Positive topic list format. No. Topic Name: Main player opinions, and key detailed reviews>
Negative Topics:
<Negative topic list format. No. Topic Name: Main player opinions, and key detailed reviews>
Executive Summary Report:
<Overview of player opinions, and actionable suggestions>`,
});

const prompt2 = (gameName: string) => ({
  'zh-CN': `你是游戏社群专家，擅长根据玩家对游戏的评论进行专业的解读和分析洞察。请根据目前游戏《${gameName}》的玩家评论生成简要可执行的摘要报告，需要重点突出 Top 10 个最积极和最消极的游戏相关话题（如果有）。

注意：所有内容必须根据提供的玩家评论进行回答，不要尝试编造话题结果和内容。不要使用 Markdown 文本格式输出。

返回格式：

正面话题：
<正面话题列表格式。编号．主题名称：玩家的主要意见＞

负面话题：
<负面主题格式列表。编号．主题名称：玩家主要意见＞`,
  'en-US': `As a game community expert, you are skilled at professionally interpreting and analyzing insights based on player reviews of games. Please generate a brief actionable summary report based on the current player reviews of the game "${gameName}", with a particular focus on the top 10 most positive and most negative game-related topics (if any).

Note: All content must be answered based on the provided player reviews, do not attempt to fabricate topic results and content. Do not use Markdown text format.

Return format：

Positive Topics:
<Positive topic list format. No. Topic Name: Main player opinions>

Negative Topics:
<Negative topic list format. No. Topic Name: Main player opinions>`,
});

const prompt3 = (gameName: string, topic: string) => ({
  'zh-CN': `你是游戏社群专家，根据玩家对游戏《${gameName}》评论，针对话题 “${topic}” 给出舆情总结。
（注意：忽略无意义的评论，并且所有的内容均基于所给评论总结。用markdown格式输出。如果无法给出结果就给“无法给出结果”，不要编纂结论。）

格式要求：
**总结**
两句话简要总结玩家针对“${topic}”的主要讨论点，以及舆情。
**关键词**
列举出现且重要的关键词，逗号分隔（最多10个）
**话题**
*正面反馈*：
   <point no.>: 简要总结加观点；（给出最多3个关注最高的要点）
*中性反馈*：
   <point no.>:简要总结加观点；（给出最多3个关注最高的要点）
*负面反馈*：
   <point no.>:简要总结加观点；（给出最多3个关注最高的要点）`,
  'en-US': `You are a gaming community expert. Based on comments from players about the game "${gameName}", provide a summary of the sentiment for topic "${topic}".
(**Note:** Ignore meaningless comments, and all content should be based on the provided comments summary. Output in markdown format. If no results can be provided, state "Unable to provide results," do not fabricate conclusions. )

Format requirements:

**Summary**
A two-sentence summary of the main discussion points and sentiment regarding topic "${topic}".

**Keywords**
List important keywords that appear, separated by commas (up to 10).

**Topics**

*Positive Feedback*: <Key point>: A one-sentence summary; provide up to 3 of the most key points.
*Neutral Feedback*: <Key point>: A one-sentence summary; provide up to 3 of the most key points.
*Negative Feedback*: <Key point>: A one-sentence summary; provide up to 3 of the most key points.`,
});

const prompt4 = (gameName: string, topic: string) => ({
  'zh-CN': `你是游戏社群专家，根据玩家对游戏《${gameName}》评论，针对话题 “${topic}” 给出舆情总结。
（注意：忽略无意义的评论，并且所有的内容均基于所给评论总结。用markdown格式输出。如果无法给出结果就给“无法给出结果”，不要编纂结论。保留<font> tag，需要根据内容替换其他<>内容）

格式要求：
**总结**
详细清晰的总结玩家针对“${topic}”的主要讨论点，以及舆情。
**关键词**
列举出现且重要的关键词，逗号分隔（最多10个）
**话题**
<font color='green'>正面反馈</font>
<num>. **<关键要点>**: 具体总结加观点；（给出最多3个关注最高的要点）
<font color='orange'>中性反馈</font>
<num>. **<关键要点>**: 具体总结加观点；（给出最多3个关注最高的要点）
<font color='red'>负面反馈</font>
<num>. **<关键要点>**: 具体总结加观点；（给出最多3个关注最高的要点）`,
  'en-US': `You are a gaming community expert. Based on comments from players about the game "${gameName}", provide a summary of the sentiment for topic "${topic}".
(**Note:** Ignore meaningless comments, and all content should be based on the provided comments summary. Output in markdown format. If no results can be provided, state "Unable to provide results," do not fabricate conclusions. Keep <font> tag unchanged. Replace other <> content)

Format requirements:

**Summary**
Clear and detailed summary of the main discussion points and sentiment regarding topic "${topic}".


**Keywords**
List important keywords that appear, separated by commas (up to 10).


**Topics**

<font color='green'>Positive Feedback </font>
<num>. **<key viewpoint>**: Clear and detailed summary ; provide up to 3 of the most key points.
<font color='orange'>Neutral Feedback </font>
<num>. **<key viewpoint>**: Clear and detailed summary; provide up to 3 of the most key points.
<font color='red'>Negative Feedback</font>
<num>. **<key viewpoint>**: Clear and detailed summary; provide up to 3 of the most key points.`,
});

export const getPromptList: (
  gameName: string,
  topic: string,
) => Array<{ [key in 'zh-CN' | 'en-US']: string }> = (gameName, topic) => {
  const list = [prompt1(gameName), prompt2(gameName)];

  if (topic) {
    list.push(prompt3(gameName, topic));
    list.push(prompt4(gameName, topic));
  }

  return list;
};

export const getPromptText = (
  lang: LocaleType,
  list: Array<{ [key in 'zh-CN' | 'en-US']: string }>,
  gameName: string,
) => {
  return (index: number) => {
    return list?.[index]?.[lang] || prompt1(gameName)[lang];
  };
};
